import { call, put, takeEvery, takeLatest, all } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';

// Services - Dynamic import
import { importReadingService } from '@/utils/dynamicImports';

// Reading slice actions
import {
    fetchReadingPassagesRequest,
    fetchReadingPassagesSuccess,
    fetchReadingPassagesFailure,
    fetchReadingPassageByIdRequest,
    fetchReadingPassageByIdSuccess,
    fetchReadingPassageByIdFailure,
    fetchPassageQuestionsRequest,
    fetchPassageQuestionsSuccess,
    fetchPassageQuestionsFailure,
    fetchReadingRecommendationsRequest,
    fetchReadingRecommendationsSuccess,
    fetchReadingRecommendationsFailure,
    rateReadingRecommendationRequest,
    rateReadingRecommendationSuccess,
    rateReadingRecommendationFailure,
    fetchUserReadingProfileRequest,
    fetchUserReadingProfileSuccess,
    fetchUserReadingProfileFailure,
    updateUserReadingProfileRequest,
    updateUserReadingProfileSuccess,
    updateUserReadingProfileFailure,
} from '../slices/reading/readingSlice';

// Reading sessions slice actions
import {
    fetchReadingSessionsRequest,
    fetchReadingSessionsSuccess,
    fetchReadingSessionsFailure,
    fetchReadingSessionByIdRequest,
    fetchReadingSessionByIdSuccess,
    fetchReadingSessionByIdFailure,
    startReadingSessionRequest,
    startReadingSessionSuccess,
    startReadingSessionFailure,
    completeReadingSessionRequest,
    completeReadingSessionSuccess,
    completeReadingSessionFailure,
    uploadReadingAudioRequest,
    uploadReadingAudioSuccess,
    uploadReadingAudioFailure,
    fetchSessionAttemptsRequest,
    fetchSessionAttemptsSuccess,
    fetchSessionAttemptsFailure,
    submitComprehensionRequest,
    submitComprehensionSuccess,
    submitComprehensionFailure,
    fetchSessionComprehensionRequest,
    fetchSessionComprehensionSuccess,
    fetchSessionComprehensionFailure,
    fetchSessionFeedbackRequest,
    fetchSessionFeedbackSuccess,
    fetchSessionFeedbackFailure,
    generateSessionFeedbackRequest,
    generateSessionFeedbackSuccess,
    generateSessionFeedbackFailure,
} from '../slices/reading/readingSessionsSlice';

// Reading analytics slice actions
import {
    fetchReadingAnalyticsRequest,
    fetchReadingAnalyticsSuccess,
    fetchReadingAnalyticsFailure,
    fetchWeeklyProgressRequest,
    fetchWeeklyProgressSuccess,
    fetchWeeklyProgressFailure,
    fetchMonthlyProgressRequest,
    fetchMonthlyProgressSuccess,
    fetchMonthlyProgressFailure,
    fetchSkillBreakdownRequest,
    fetchSkillBreakdownSuccess,
    fetchSkillBreakdownFailure,
    fetchWaveformDataRequest,
    fetchWaveformDataSuccess,
    fetchWaveformDataFailure,
    compareReadingAudioRequest,
    compareReadingAudioSuccess,
    compareReadingAudioFailure,
    fetchProgressTimelineRequest,
    fetchProgressTimelineSuccess,
    fetchProgressTimelineFailure,
    fetchStreakDataRequest,
    fetchStreakDataSuccess,
    fetchStreakDataFailure,
    fetchSkillTrendsRequest,
    fetchSkillTrendsSuccess,
    fetchSkillTrendsFailure,
} from '../slices/reading/readingAnalyticsSlice';

import type {
    ReadingPassageFilters,
    StartReadingSessionRequest,
    AudioUploadRequest,
    SubmitComprehensionRequest,
    PaginationParams
} from '@/services/reading/types';

// ================================
// READING PASSAGES SAGAS
// ================================

function* fetchReadingPassagesSaga(action: PayloadAction<ReadingPassageFilters | undefined>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getReadingPassages, action.payload || {});
        yield put(fetchReadingPassagesSuccess(response));
    } catch (error: any) {
        yield put(fetchReadingPassagesFailure(error.message || 'Failed to fetch reading passages'));
    }
}

function* fetchReadingPassageByIdSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getReadingPassage, action.payload);
        yield put(fetchReadingPassageByIdSuccess(response));
    } catch (error: any) {
        yield put(fetchReadingPassageByIdFailure(error.message || 'Failed to fetch reading passage'));
    }
}

function* fetchPassageQuestionsSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getPassageQuestions, action.payload);
        yield put(fetchPassageQuestionsSuccess(response));
    } catch (error: any) {
        yield put(fetchPassageQuestionsFailure(error.message || 'Failed to fetch passage questions'));
    }
}

function* fetchReadingRecommendationsSaga(action: PayloadAction<number | undefined>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getRecommendations);
        yield put(fetchReadingRecommendationsSuccess(response));
    } catch (error: any) {
        yield put(fetchReadingRecommendationsFailure(error.message || 'Failed to fetch recommendations'));
    }
}

function* rateReadingRecommendationSaga(action: PayloadAction<{ recommendationId: string; rating: number }>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        yield call(readingService.rateRecommendation, action.payload.recommendationId, action.payload.rating);
        yield put(rateReadingRecommendationSuccess());
    } catch (error: any) {
        yield put(rateReadingRecommendationFailure(error.message || 'Failed to rate recommendation'));
    }
}

function* fetchUserReadingProfileSaga(): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getUserReadingProfile);
        yield put(fetchUserReadingProfileSuccess(response));
    } catch (error: any) {
        yield put(fetchUserReadingProfileFailure(error.message || 'Failed to fetch user profile'));
    }
}

function* updateUserReadingProfileSaga(action: PayloadAction<{ preferred_topics?: string[]; preferred_passage_types?: string[]; reading_goals?: any }>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.updateUserReadingProfile, action.payload as any);
        yield put(updateUserReadingProfileSuccess(response));
    } catch (error: any) {
        yield put(updateUserReadingProfileFailure(error.message || 'Failed to update user profile'));
    }
}

// ================================
// READING SESSIONS SAGAS
// ================================

function* fetchReadingSessionsSaga(action: PayloadAction<{ page?: number; pageSize?: number }>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getUserReadingSessions);
        yield put(fetchReadingSessionsSuccess(response));
    } catch (error: any) {
        yield put(fetchReadingSessionsFailure(error.message || 'Failed to fetch reading sessions'));
    }
}

function* fetchReadingSessionByIdSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getReadingSession, action.payload);
        yield put(fetchReadingSessionByIdSuccess(response));
    } catch (error: any) {
        yield put(fetchReadingSessionByIdFailure(error.message || 'Failed to fetch reading session'));
    }
}

function* startReadingSessionSaga(action: PayloadAction<StartReadingSessionRequest>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.startReadingSession, action.payload);
        yield put(startReadingSessionSuccess(response));
    } catch (error: any) {
        yield put(startReadingSessionFailure(error.message || 'Failed to start reading session'));
    }
}

function* completeReadingSessionSaga(action: PayloadAction<{ sessionId: string; completionData: any }>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.completeReadingSession, action.payload.sessionId, action.payload.completionData);
        yield put(completeReadingSessionSuccess(response));
    } catch (error: any) {
        yield put(completeReadingSessionFailure(error.message || 'Failed to complete reading session'));
    }
}

function* uploadReadingAudioSaga(action: PayloadAction<AudioUploadRequest>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.uploadAudio, action.payload);

        // Handle the response structure from backend
        if (response.success) {
            yield put(uploadReadingAudioSuccess({
                attempt: response.attempt,
                session_progress: response.session_progress,
                is_passing: response.is_passing,
                passing_threshold: response.passing_threshold,
                session: response.session,
            }));
        } else {
            yield put(uploadReadingAudioFailure(response.message || 'Failed to upload audio'));
        }
    } catch (error: any) {
        yield put(uploadReadingAudioFailure(error.message || 'Failed to upload audio'));
    }
}

function* fetchSessionAttemptsSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getSessionAttempts, action.payload);
        yield put(fetchSessionAttemptsSuccess(response));
    } catch (error: any) {
        yield put(fetchSessionAttemptsFailure(error.message || 'Failed to fetch session attempts'));
    }
}

function* submitComprehensionSaga(action: PayloadAction<SubmitComprehensionRequest>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.submitComprehension, action.payload);
        yield put(submitComprehensionSuccess(response));
    } catch (error: any) {
        yield put(submitComprehensionFailure(error.message || 'Failed to submit comprehension answers'));
    }
}

function* fetchSessionComprehensionSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getSessionComprehension, action.payload);
        yield put(fetchSessionComprehensionSuccess(response));
    } catch (error: any) {
        yield put(fetchSessionComprehensionFailure(error.message || 'Failed to fetch session comprehension'));
    }
}

function* fetchSessionFeedbackSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getSessionFeedback, action.payload);
        yield put(fetchSessionFeedbackSuccess(response));
    } catch (error: any) {
        yield put(fetchSessionFeedbackFailure(error.message || 'Failed to fetch session feedback'));
    }
}

function* generateSessionFeedbackSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.generateSessionFeedback, action.payload);
        yield put(generateSessionFeedbackSuccess(response));
    } catch (error: any) {
        yield put(generateSessionFeedbackFailure(error.message || 'Failed to generate session feedback'));
    }
}

// ================================
// READING ANALYTICS SAGAS
// ================================

function* fetchReadingAnalyticsSaga(action: PayloadAction<number>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getAnalytics, action.payload || 30);
        yield put(fetchReadingAnalyticsSuccess(response));
    } catch (error: any) {
        yield put(fetchReadingAnalyticsFailure(error.message || 'Failed to fetch reading analytics'));
    }
}

function* fetchWeeklyProgressSaga(action: PayloadAction<number>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getWeeklyProgress, action.payload || 12);
        yield put(fetchWeeklyProgressSuccess(response));
    } catch (error: any) {
        yield put(fetchWeeklyProgressFailure(error.message || 'Failed to fetch weekly progress'));
    }
}

function* fetchMonthlyProgressSaga(action: PayloadAction<number>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getMonthlyProgress, action.payload || 6);
        yield put(fetchMonthlyProgressSuccess(response));
    } catch (error: any) {
        yield put(fetchMonthlyProgressFailure(error.message || 'Failed to fetch monthly progress'));
    }
}

function* fetchSkillBreakdownSaga(): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getSkillBreakdown);
        yield put(fetchSkillBreakdownSuccess(response));
    } catch (error: any) {
        yield put(fetchSkillBreakdownFailure(error.message || 'Failed to fetch skill breakdown'));
    }
}

function* fetchWaveformDataSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getWaveformData, action.payload);
        yield put(fetchWaveformDataSuccess(response));
    } catch (error: any) {
        yield put(fetchWaveformDataFailure(error.message || 'Failed to fetch waveform data'));
    }
}

function* compareReadingAudioSaga(action: PayloadAction<{ userAudioUrl: string; referenceAudioUrl?: string; targetSentence?: string }>): SagaIterator {
    try {
        const { userAudioUrl, referenceAudioUrl, targetSentence } = action.payload;
        const { default: readingService } = yield call(importReadingService);
        //@ts-ignore
        const response = yield call(readingService.compareAudio, userAudioUrl, referenceAudioUrl, targetSentence);
        yield put(compareReadingAudioSuccess(response));
    } catch (error: any) {
        yield put(compareReadingAudioFailure(error.message || 'Failed to compare audio'));
    }
}

function* fetchProgressTimelineSaga(action: PayloadAction<number>): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getProgressTimeline, action.payload || 30);
        yield put(fetchProgressTimelineSuccess(response));
    } catch (error: any) {
        yield put(fetchProgressTimelineFailure(error.message || 'Failed to fetch progress timeline'));
    }
}

function* fetchStreakDataSaga(): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getStreakData);
        yield put(fetchStreakDataSuccess(response));
    } catch (error: any) {
        yield put(fetchStreakDataFailure(error.message || 'Failed to fetch streak data'));
    }
}

function* fetchSkillTrendsSaga(): SagaIterator {
    try {
        const { default: readingService } = yield call(importReadingService);
        const response = yield call(readingService.getSkillTrends);
        yield put(fetchSkillTrendsSuccess(response));
    } catch (error: any) {
        yield put(fetchSkillTrendsFailure(error.message || 'Failed to fetch skill trends'));
    }
}

// ================================
// WATCHER SAGAS
// ================================

function* watchReadingPassages() {
    yield takeLatest(fetchReadingPassagesRequest.type, fetchReadingPassagesSaga);
    yield takeLatest(fetchReadingPassageByIdRequest.type, fetchReadingPassageByIdSaga);
    yield takeLatest(fetchPassageQuestionsRequest.type, fetchPassageQuestionsSaga);
}

function* watchReadingRecommendations() {
    yield takeLatest(fetchReadingRecommendationsRequest.type, fetchReadingRecommendationsSaga);
    yield takeEvery(rateReadingRecommendationRequest.type, rateReadingRecommendationSaga);
}

function* watchUserProfile() {
    yield takeLatest(fetchUserReadingProfileRequest.type, fetchUserReadingProfileSaga);
    yield takeLatest(updateUserReadingProfileRequest.type, updateUserReadingProfileSaga);
}

function* watchReadingSessions() {
    yield takeLatest(fetchReadingSessionsRequest.type, fetchReadingSessionsSaga);
    yield takeLatest(fetchReadingSessionByIdRequest.type, fetchReadingSessionByIdSaga);
    yield takeLatest(startReadingSessionRequest.type, startReadingSessionSaga);
    yield takeLatest(completeReadingSessionRequest.type, completeReadingSessionSaga);
}

function* watchAudioUpload() {
    yield takeEvery(uploadReadingAudioRequest.type, uploadReadingAudioSaga);
    yield takeLatest(fetchSessionAttemptsRequest.type, fetchSessionAttemptsSaga);
}

function* watchComprehension() {
    yield takeLatest(submitComprehensionRequest.type, submitComprehensionSaga);
    yield takeLatest(fetchSessionComprehensionRequest.type, fetchSessionComprehensionSaga);
}

function* watchFeedback() {
    yield takeLatest(fetchSessionFeedbackRequest.type, fetchSessionFeedbackSaga);
    yield takeLatest(generateSessionFeedbackRequest.type, generateSessionFeedbackSaga);
}

function* watchAnalytics() {
    yield takeLatest(fetchReadingAnalyticsRequest.type, fetchReadingAnalyticsSaga);
    yield takeLatest(fetchWeeklyProgressRequest.type, fetchWeeklyProgressSaga);
    yield takeLatest(fetchMonthlyProgressRequest.type, fetchMonthlyProgressSaga);
    yield takeLatest(fetchSkillBreakdownRequest.type, fetchSkillBreakdownSaga);
}

function* watchWaveformAndAudio() {
    yield takeLatest(fetchWaveformDataRequest.type, fetchWaveformDataSaga);
    yield takeLatest(compareReadingAudioRequest.type, compareReadingAudioSaga);
}

function* watchProgressTracking() {
    yield takeLatest(fetchProgressTimelineRequest.type, fetchProgressTimelineSaga);
    yield takeLatest(fetchStreakDataRequest.type, fetchStreakDataSaga);
    yield takeLatest(fetchSkillTrendsRequest.type, fetchSkillTrendsSaga);
}

// ================================
// ROOT READING SAGA
// ================================

export default function* readingSaga() {
    yield all([
        watchReadingPassages(),
        watchReadingRecommendations(),
        watchUserProfile(),
        watchReadingSessions(),
        watchAudioUpload(),
        watchComprehension(),
        watchFeedback(),
        watchAnalytics(),
        watchWaveformAndAudio(),
        watchProgressTracking(),
    ]);
} 